import { FormControl, FormField } from '@/components/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Form, FormInput, FormItem, FormLabel, FormMessage } from '@/components/wm';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { API_DATE_FORMAT, isLevelIndicator } from '../utils';
import { savedIndicatorSite, SiteIndicatorFields } from './SiteIndicatorFields';
import { Widget } from './Widget';

const toInput = value => (Number.isFinite(value) ? String(value) : '');

const toNumber = value => (value === '' ? NaN : Number(value));

// Nothing records a tank's capacity, and the indicator's max_value is a
// validation bound (1.8e19 by default), not the highest reading.
function fetchHighestReading(indicator) {
    const
        to = moment.tz(indicator.timezone()),
        from = to.clone().subtract(12, 'months');

    return indicator.getRealtimeData({
        from: from.format(API_DATE_FORMAT),
        rate: 3600,
        to: to.format(API_DATE_FORMAT)
    }).then(response => {
        const values = (Array.isArray(response?.data) ? response.data : [])
            .map(point => point.v)
            .filter(value => value !== null && value !== undefined);

        return values.length ? Math.max(...values) : null;
    });
}

export function NewLevelData({ data, onChangeField, onCompleted }) {
    const
        decimalOptions = ['0', '1', '2', '3'],
        unitScaleOptions = [
            { label: i18next.t('unit_scales.auto'), value: -1 },
            { label: i18next.t('unit_scales.full'), value: 0 },
            { label: i18next.t('unit_scales.thousands'), value: 3 },
            { label: i18next.t('unit_scales.millions'), value: 6 },
            { label: i18next.t('unit_scales.billions'), value: 9 }
        ],
        indicator = WiseMetering.indicators.get(data.data?.indicatorId),
        unit = indicator?.get('unit'),
        [highest, setHighest] = useState(null);

    useEffect(() => {
        setHighest(null);

        if (!indicator) {
            return;
        }

        let cancelled = false;

        fetchHighestReading(indicator)
            .then(value => !cancelled && setHighest(value))
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [indicator]);

    const
        required = i18next.t('form_validations.generic.required'),
        number = z.string().min(1, required).refine(value => Number.isFinite(toNumber(value)), required);

    const formSchema = z.object({
        decimals: z.string().min(1, required),
        indicatorId: z.string().min(1, i18next.t('form_validations.generic.indicator_required')),
        max: number,
        min: number,
        site: z.string().min(1, required),
        unitScale: z.coerce.number().int(required)
    // Not ordered: a sensor that reads the distance down to the surface has its
    // full reading below its empty one.
    }).refine(values => toNumber(values.max) !== toNumber(values.min), {
        message: i18next.t('dashboard.custom_dashboard.level_full_differs'),
        path: ['max']
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            decimals: data.data?.decimals || '2',
            indicatorId: data.data?.indicatorId || '',
            max: toInput(data.data?.max),
            min: toInput(data.data?.min),
            site: savedIndicatorSite(data.data?.indicatorId),
            unitScale: data.data?.unitScale ?? -1
        },
        mode: 'onChange'
    });

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    const setData = changes => onChangeField('data')({ ...data.data, ...changes });

    const rangeField = name => (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    <FormLabel>
                        {i18next.t(`dashboard.custom_dashboard.${name === 'min' ? 'level_empty_at' : 'level_full_at'}`)}{unit ? ` (${unit})` : ''}
                    </FormLabel>
                    <FormControl>
                        <FormInput
                            {...field}
                            onChange={e => {
                                field.onChange(e);
                                setData({ [name]: toNumber(e.target.value) });
                                form.trigger(['min', 'max']);
                            }}
                            placeholder={name === 'max' && highest !== null ? String(Math.round(highest * 100) / 100) : undefined}
                            step="any"
                            type="number"
                        />
                    </FormControl>
                    <FormMessage/>
                </FormItem>
            )}
        />
    );

    return (
        <div className="tw-flex tw-gap-6">
            <div className="tw-flex-1">
                <Form {...form}>
                    <SiteIndicatorFields
                        data={data}
                        emptyLabel={i18next.t('dashboard.custom_dashboard.no_level_indicators')}
                        filter={isLevelIndicator}
                        form={form}
                        onChangeField={onChangeField}
                        onIndicatorChange={(_, indicatorId) => setData({ indicatorId })}
                    />
                    <div className="tw-flex tw-gap-4">
                        <div className="tw-flex-1">{rangeField('min')}</div>
                        <div className="tw-flex-1">{rangeField('max')}</div>
                    </div>
                    {highest !== null && (
                        <div className="tw-ml-2 tw-text-xs tw-text-muted-foreground">
                            {i18next.t('dashboard.custom_dashboard.level_highest_reading', {
                                interpolation: { escapeValue: false },
                                value: formatValue(highest, unit, 2)
                            })}
                        </div>
                    )}
                    <FormField
                        control={form.control}
                        name="unitScale"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('unit_scales.auto').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(Number(val));
                                            setData({ unitScale: Number(val) });
                                        }}
                                        value={String(field.value)}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('unit_scales.auto')}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {unitScaleOptions.map(unitScale => (
                                                <SelectItem key={unitScale.value} value={String(unitScale.value)}>
                                                    {unitScale.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="decimals"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.decimals').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);
                                            setData({ decimals: val });
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('glossary.decimals').capitalize()}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {decimalOptions.map(decimal => (
                                                <SelectItem key={decimal} value={decimal}>
                                                    {decimal}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </Form>
            </div>
            <div className="tw-w-[320px] tw-h-[200px] tw-flex-shrink-0">
                <Widget widget={data}/>
            </div>
        </div>
    );
}
